'use client'

import { useState, useEffect, useRef } from 'react'
import { createClient } from '@/lib/supabase/client'
import { markAlertRead, dismissAlert, markAllAlertsRead } from '@/lib/actions/alerts'
import type { Alert, MemberAlert } from '@/lib/types/database'

interface AlertBellProps {
  memberId: string
  initialAlerts: (MemberAlert & { alert: Alert })[]
  initialCount: number
}

const ALERT_TYPE_STYLES = {
  info: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  warning: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
  urgent: 'bg-red-500/10 border-red-500/30 text-red-400',
  success: 'bg-green-500/10 border-green-500/30 text-green-400',
}

const ALERT_TYPE_ICONS = {
  info: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  warning: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  urgent: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  success: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
}

export function AlertBell({ memberId, initialAlerts, initialCount }: AlertBellProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [alerts, setAlerts] = useState(initialAlerts)
  const [unreadCount, setUnreadCount] = useState(initialCount)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Subscribe to realtime updates
  useEffect(() => {
    const supabase = createClient()

    const channel = supabase
      .channel('member_alerts_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'member_alerts',
          filter: `member_id=eq.${memberId}`,
        },
        async () => {
          // Refetch alerts on any change
          const { data } = await supabase
            .from('member_alerts')
            .select(`
              *,
              alert:alerts(*)
            `)
            .eq('member_id', memberId)
            .eq('is_dismissed', false)
            .order('created_at', { ascending: false })

          if (data) {
            const now = new Date()
            const activeAlerts = data.filter(ma => {
              const alert = ma.alert as Alert
              if (alert.status !== 'active') return false
              if (alert.expires_at && new Date(alert.expires_at) < now) return false
              return true
            }) as (MemberAlert & { alert: Alert })[]

            setAlerts(activeAlerts)
            setUnreadCount(activeAlerts.filter(a => !a.is_read).length)
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [memberId])

  const handleMarkRead = async (memberAlertId: string) => {
    await markAlertRead(memberAlertId)
    setAlerts(prev =>
      prev.map(a =>
        a.id === memberAlertId ? { ...a, is_read: true, read_at: new Date().toISOString() } : a
      )
    )
    setUnreadCount(prev => Math.max(0, prev - 1))
  }

  const handleDismiss = async (memberAlertId: string) => {
    await dismissAlert(memberAlertId)
    setAlerts(prev => prev.filter(a => a.id !== memberAlertId))
  }

  const handleMarkAllRead = async () => {
    await markAllAlertsRead()
    setAlerts(prev => prev.map(a => ({ ...a, is_read: true, read_at: new Date().toISOString() })))
    setUnreadCount(0)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-2)] rounded-md transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-micro font-medium bg-[var(--club-primary)] text-[var(--club-button-text)] rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[var(--surface-1)] border border-[var(--border)] rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
            <h3 className="text-body-sm font-medium text-[var(--text-primary)]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-micro text-[var(--club-primary)] hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Alert List */}
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <svg className="w-8 h-8 mx-auto mb-2 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-caption text-[var(--text-muted)]">No notifications</p>
              </div>
            ) : (
              alerts.map(({ id, alert, is_read }) => (
                <div
                  key={id}
                  className={`px-4 py-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--surface-2)] transition-colors ${
                    !is_read ? 'bg-[var(--club-primary)]/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`mt-0.5 p-1.5 rounded-md border ${ALERT_TYPE_STYLES[alert.alert_type]}`}>
                      {ALERT_TYPE_ICONS[alert.alert_type]}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className={`text-body-sm ${!is_read ? 'font-medium' : ''} text-[var(--text-primary)]`}>
                          {alert.title}
                        </h4>
                        {!is_read && (
                          <span className="w-2 h-2 rounded-full bg-[var(--club-primary)] flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-caption text-[var(--text-secondary)] line-clamp-2 mt-0.5">
                        {alert.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        {!is_read && (
                          <button
                            onClick={() => handleMarkRead(id)}
                            className="text-micro text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                          >
                            Mark read
                          </button>
                        )}
                        <button
                          onClick={() => handleDismiss(id)}
                          className="text-micro text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
